import React, { createContext, useContext, useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { useLocation } from "@reach/router"

interface LanguageContextType {
  currentLanguage: string
  switchLanguage: (lang: string) => void
  getLocalizedPath: (path: string) => string
}

const LanguageContext = createContext<LanguageContextType | undefined>(
  undefined
)

export const useLanguage = () => {
  const context = useContext(LanguageContext)
  if (!context) {
    throw new Error("useLanguage must be used within a LanguageProvider")
  }
  return context
}

interface LanguageProviderProps {
  children: React.ReactNode
}

const languageFromPath = (pathname: string): string => {
  if (pathname === "/sv" || pathname.startsWith("/sv/")) return "sv"
  if (pathname === "/no" || pathname.startsWith("/no/")) return "no"
  if (pathname === "/da" || pathname.startsWith("/da/")) return "da"
  if (pathname === "/nl" || pathname.startsWith("/nl/")) return "nl"
  if (pathname === "/de" || pathname.startsWith("/de/")) return "de"
  if (pathname === "/fr" || pathname.startsWith("/fr/")) return "fr"
  if (pathname === "/es" || pathname.startsWith("/es/")) return "es"
  if (pathname === "/pt-pt" || pathname.startsWith("/pt-pt/")) return "pt-PT"
  if (pathname === "/pt-br" || pathname.startsWith("/pt-br/")) return "pt-BR"
  if (pathname === "/it" || pathname.startsWith("/it/")) return "it"
  if (pathname === "/fi" || pathname.startsWith("/fi/")) return "fi"
  if (pathname === "/tr" || pathname.startsWith("/tr/")) return "tr"
  if (pathname === "/uk" || pathname.startsWith("/uk/")) return "uk"
  if (pathname === "/ko" || pathname.startsWith("/ko/")) return "ko"
  if (pathname === "/pl" || pathname.startsWith("/pl/")) return "pl"
  if (pathname === "/zh" || pathname.startsWith("/zh/")) return "zh"
  if (pathname === "/ru" || pathname.startsWith("/ru/")) return "ru"
  return "en" // Root level is English
}

export const LanguageProvider: React.FC<LanguageProviderProps> = ({
  children,
}) => {
  const { i18n } = useTranslation()
  const location = useLocation()

  // Derive the active language from the URL path. useLocation is consistent
  // between SSR and the client, so seeding state from it keeps the language
  // switcher identical on both sides and avoids hydration mismatches (the
  // i18n singleton's language is unreliable at provider-init time during SSR).
  const pathLanguage = languageFromPath(location?.pathname || "/")
  const [currentLanguage, setCurrentLanguage] = useState(pathLanguage)

  // Follow client-side route navigations to a different-language page.
  useEffect(() => {
    if (pathLanguage !== currentLanguage) {
      setCurrentLanguage(pathLanguage)
      i18n.changeLanguage(pathLanguage)
    }
  }, [pathLanguage])

  useEffect(() => {
    if (typeof document !== "undefined") {
      document.documentElement.lang =
        currentLanguage === "no" ? "nb" : currentLanguage
    }
  }, [currentLanguage])

  const stripLocale = (pathname: string) => {
    const segments = pathname.split("/").filter(Boolean)
    const hasLocale =
      segments[0] === "sv" ||
      segments[0] === "en" ||
      segments[0] === "no" ||
      segments[0] === "da" ||
      segments[0] === "fr" ||
      segments[0] === "es" ||
      segments[0] === "nl" ||
      segments[0] === "de" ||
      segments[0] === "pt-pt" ||
      segments[0] === "pt-br" ||
      segments[0] === "it" ||
      segments[0] === "fi" ||
      segments[0] === "tr" ||
      segments[0] === "uk" ||
      segments[0] === "ko" ||
      segments[0] === "pl" ||
      segments[0] === "zh" ||
      segments[0] === "ru"
    return hasLocale ? segments.slice(1) : segments
  }

  const buildPath = (segments: string[]) => {
    return segments.length === 0 ? "/" : `/${segments.join("/")}`
  }

  const localizeSegments = (lang: string, base: string[]) => {
    const routeLocale =
      lang === "pt-PT" ? "pt-pt" : lang === "pt-BR" ? "pt-br" : lang
    const section = base[0] || ""
    // English-only pages exist solely at the root path — /sv/whats-new and
    // /no/whats-new are static redirects, not client routes, so never prefix.
    if (section === "whats-new") return base
    if (section === "newsroom" && !["sv", "no", "da"].includes(lang))
      return base
    if (
      ["pianissimo", "pianissimo-sv", "pianissimo-da"].includes(section) &&
      !["sv", "no", "da"].includes(lang)
    )
      return base
    // Dutch blog content is intentionally the English catalog. Keep its
    // listing canonical at /blog instead of presenting /nl/blog as localized.
    if (
      [
        "nl",
        "fr",
        "es",
        "pt-PT",
        "pt-BR",
        "it",
        "fi",
        "ru",
        "zh",
        "pl",
        "ko",
        "uk",
        "tr",
      ].includes(lang) &&
      section === "blog"
    )
      return base
    // German editorial content is not published yet. Keep blog and product
    // navigation on the canonical English catalogs.
    if (
      [
        "de",
        "fr",
        "es",
        "pt-PT",
        "pt-BR",
        "it",
        "fi",
        "ru",
        "zh",
        "pl",
        "ko",
        "uk",
        "tr",
      ].includes(lang) &&
      (section === "blog" || section === "product")
    ) {
      return base
    }
    // Danish has a blog but no translated product catalog.
    if (lang === "da" && section === "product") return base
    // Blog and product pages follow same pattern: /blog, /sv/blog, /no/blog
    if (section === "blog" || section === "product") {
      if (lang === "en") return base // English at root: /blog/slug, /product/slug
      return [routeLocale, ...base] // Swedish/Norwegian: /sv/blog/slug, /no/blog/slug
    }
    // Other pages
    if (lang === "en") return base
    return [routeLocale, ...base]
  }

  const switchLanguage = (lang: string) => {
    if (lang === currentLanguage) return

    const routeLocale =
      lang === "pt-PT" ? "pt-pt" : lang === "pt-BR" ? "pt-br" : lang

    if (typeof window !== "undefined") {
      const currentPath = window.location.pathname
      const strippedSegments = stripLocale(currentPath)
      const normalizedPath = `/${currentPath
        .split("?")[0]
        .split("#")[0]
        .replace(/^\/|\/$/g, "")}/`
      const transcriptionPaths = {
        en: "/transcribe/",
        sv: "/sv/transkribera/",
        no: "/no/transkribere/",
        da: "/da/transskribering/",
        fr: "/fr/transcrire/",
        es: "/es/transcribir/",
        nl: "/nl/transcriberen/",
        de: "/de/transkribieren/",
        "pt-PT": "/pt-pt/transcrever/",
        "pt-BR": "/pt-br/transcrever/",
        it: "/it/trascrivere/",
        fi: "/fi/litterointi/",
        tr: "/tr/ses-kaydini-yaziya-cevirme/",
        uk: "/uk/audio-v-tekst/",
        ko: "/ko/speech-to-text/",
        pl: "/pl/transkrypcja/",
        zh: "/zh/speech-to-text/",
        ru: "/ru/transkribatsiya/",
      } as const
      // Locale-only SEO pages (GuideLandingPage sets data-locale-only) have no
      // counterpart elsewhere, so switching language from them goes to that
      // language's transcription hub.
      const isLocaleOnlyPage =
        document.documentElement.dataset.localeOnly === "true"
      const isTranscriptionPage =
        Object.values(transcriptionPaths).includes(
          normalizedPath as (typeof transcriptionPaths)[keyof typeof transcriptionPaths]
        ) || isLocaleOnlyPage

      // Handle root pages (/, /sv, /no)
      const isRootPage = strippedSegments.length === 0
      const newPath =
        isTranscriptionPage && lang in transcriptionPaths
          ? transcriptionPaths[lang as keyof typeof transcriptionPaths]
          : isRootPage
          ? lang === "sv"
            ? "/sv"
            : lang === "no"
            ? "/no"
            : lang === "da"
            ? "/da"
            : lang === "nl"
            ? "/nl"
            : lang === "de"
            ? "/de"
            : lang === "fr"
            ? "/fr"
            : lang === "es"
            ? "/es"
            : lang === "pt-PT"
            ? "/pt-pt"
            : lang === "pt-BR"
            ? "/pt-br"
            : lang === "it"
            ? "/it"
            : lang === "fi"
            ? "/fi"
            : lang === "tr"
            ? "/tr"
            : lang === "uk"
            ? "/uk"
            : lang === "ko"
            ? "/ko"
            : lang === "pl"
            ? "/pl"
            : lang === "zh"
            ? "/zh"
            : lang === "ru"
            ? "/ru"
            : "/"
          : buildPath(localizeSegments(lang, strippedSegments))

      // Check if we're on a blog/product post page
      const section = strippedSegments[0] || ""
      const isBlogOrProductPost =
        (section === "blog" || section === "product") &&
        strippedSegments.length > 1

      if (isTranscriptionPage && lang in transcriptionPaths) {
        // The hub is a different page, so a URL rewrite would leave the guide on screen
        window.location.href = newPath
      } else if (isBlogOrProductPost) {
        // Redirect to the listing page when switching language on a blog/product post
        // since translations may have different slugs
        const listingPath =
          section === "product"
            ? [
                "sv",
                "no",
                "da",
                "fr",
                "es",
                "nl",
                "de",
                "pt-PT",
                "pt-BR",
                "it",
                "fi",
                "tr",
                "uk",
                "ko",
                "pl",
                "zh",
                "ru",
              ].includes(lang)
              ? `/${routeLocale}`
              : "/"
            : [
                "en",
                "fr",
                "es",
                "nl",
                "de",
                "pt-PT",
                "pt-BR",
                "it",
                "fi",
                "tr",
                "uk",
                "ko",
                "pl",
                "zh",
                "ru",
              ].includes(lang)
            ? `/${section}`
            : lang === "da"
            ? "/da/blog"
            : `/${lang}/${section}`
        window.location.href = listingPath
      } else {
        // Load the localized Gatsby page so its pageContext, canonical URL,
        // market-specific content and HTML language all match the new route.
        window.location.href = newPath
      }
    }
  }

  const getLocalizedPath = (path: string) => {
    const segments = stripLocale(path)
    return buildPath(localizeSegments(currentLanguage, segments))
  }

  const value: LanguageContextType = {
    currentLanguage,
    switchLanguage,
    getLocalizedPath,
  }

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  )
}
